// How the cover should sound, in the /create studio's Sound sheet. Plain F
// controls (steppers, switches, pills) over the same settings reducer
// CreateModal uses (components/modals/conversionRequest.js), so the request
// body is unchanged. The defaults suit most songs; the card shows a one-line
// summary of anything changed.
import React from "react";
import axios from "axios";
import { useQuery } from "react-query";
import { Minus, Plus } from "lucide-react";
import { VoiceImage } from "./StudioChrome";

/* eslint-disable @typescript-eslint/no-explicit-any */

const PITCH_MIN = -24;
const PITCH_MAX = 24;
const VOLUME_MIN = -12;
const VOLUME_MAX = 12;
const VOLUME_STEP = 2;

// AutotuneSelector's slider ran 0.2-1; three stops cover it.
const AUTOTUNE_LEVELS = [
  { label: "Light", value: 0.4 },
  { label: "Medium", value: 0.7 },
  { label: "Strong", value: 1 },
];

const signed = (n: number) => (n > 0 ? `+${n}` : `${n}`);

// Same query key as AudioEffectsSelector, so both share the cache.
export const useEffectPresets = () =>
  useQuery({
    queryKey: ["effectPresets"],
    queryFn: async () => {
      const { data } = await axios.get("/api/effects/getEffects");
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch effects");
      }
      return data.data as any[];
    },
    staleTime: 10 * 60 * 1000,
  });

// The card's one-liner: only what differs from the defaults.
export const soundSummary = (
  settings: any,
  voices: any[],
  effects: any[] | undefined,
) => {
  const parts: string[] = [];
  const pitches = voices.map((v) => settings.pitchScaling[v._id] ?? 0);
  if (pitches.some((p) => p !== 0)) {
    parts.push(`Pitch ${pitches.map(signed).join(" / ")}`);
  }
  const effect = effects?.find((e) => e._id === settings.selectedEffectId);
  if (effect) parts.push(`${effect.emoji} ${effect.name}`);
  if (settings.autotune?.enabled) parts.push("Autotune");
  if (settings.voiceVolume)
    parts.push(`Vocals ${signed(settings.voiceVolume)}`);
  if (settings.extractBackground) parts.push("Backing vocals split");
  if (settings.skipExtraction) parts.push("Vocals only input");
  return parts.length ? parts.join(" · ") : "Original pitch, no effects";
};

const Group = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="tw-flex tw-flex-col tw-gap-2.5 tw-border-0 tw-border-b-2 tw-border-solid tw-border-jam-lav tw-pb-4 last:tw-border-b-0">
    <div>
      <div className="tw-text-[15px] tw-font-extrabold">{title}</div>
      {hint && (
        <div className="tw-text-xs tw-font-semibold tw-text-jam-muted">
          {hint}
        </div>
      )}
    </div>
    {children}
  </div>
);

const Stepper = ({
  value,
  min,
  max,
  step = 1,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  label: string;
}) => (
  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-p-1">
    <button
      type="button"
      aria-label={`Lower ${label}`}
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - step))}
      className="tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-text-jam-ink disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
    >
      <Minus size={16} strokeWidth={3} />
    </button>
    <span className="tw-w-11 tw-text-center tw-text-[15px] tw-font-extrabold tw-tabular-nums">
      {signed(value)}
    </span>
    <button
      type="button"
      aria-label={`Raise ${label}`}
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + step))}
      className="tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-text-jam-ink disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
    >
      <Plus size={16} strokeWidth={3} />
    </button>
  </div>
);

const Switch = ({
  checked,
  onChange,
  title,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  hint?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-jam-ink"
  >
    <span className="tw-min-w-0 tw-flex-grow">
      <span className="tw-block tw-text-[15px] tw-font-extrabold">{title}</span>
      {hint && (
        <span className="tw-block tw-text-xs tw-font-semibold tw-text-jam-muted">
          {hint}
        </span>
      )}
    </span>
    <span
      className={`tw-flex tw-h-7 tw-w-12 tw-shrink-0 tw-items-center tw-rounded-full tw-p-0.5 tw-transition-colors ${
        checked ? "tw-bg-jam-purple" : "tw-bg-jam-soft"
      }`}
    >
      <span
        className={`tw-h-6 tw-w-6 tw-rounded-full tw-bg-white tw-transition-transform ${
          checked ? "tw-translate-x-5" : ""
        }`}
      />
    </span>
  </button>
);

const Pill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-extrabold ${
      active
        ? "tw-bg-jam-ink tw-text-white"
        : "tw-bg-jam-lav tw-text-jam-ink hover:tw-text-jam-purple"
    }`}
  >
    {children}
  </button>
);

const SoundOptions = ({
  voices,
  song,
  settings,
  dispatchSettings,
}: {
  voices: any[];
  song: any | null;
  settings: any;
  dispatchSettings: (a: any) => void;
}) => {
  const { data: effects = [] } = useEffectPresets();
  const setPitch = (voiceId: string, pitchValue: number) =>
    dispatchSettings({
      type: "SET_PITCH_SCALING",
      payload: { voiceId, pitchValue },
    });
  const setEffect = (id: string | null) =>
    dispatchSettings({ type: "SET_AUDIO_EFFECT", payload: id });
  const effectGroups = [
    { id: "vocals_only", title: "On the vocals" },
    { id: "full_song", title: "On the whole song" },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Group
        title="Pitch"
        hint="In semitones. Going from a male to a female voice? Try +12."
      >
        {voices.map((v) => {
          const value = settings.pitchScaling[v._id] ?? 0;
          return (
            <div key={v._id} className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center tw-gap-3">
                <span className="tw-relative tw-h-9 tw-w-9 tw-shrink-0 tw-overflow-hidden tw-rounded-full">
                  <VoiceImage
                    model={v}
                    fill
                    sizes="36px"
                    className="tw-object-cover"
                  />
                </span>
                <span className="tw-min-w-0 tw-flex-grow tw-truncate tw-text-sm tw-font-extrabold">
                  {v.artistname}
                </span>
                <Stepper
                  label={`pitch for ${v.artistname}`}
                  value={value}
                  min={PITCH_MIN}
                  max={PITCH_MAX}
                  onChange={(p) => setPitch(v._id, p)}
                />
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-pl-12">
                {[
                  { label: "Male → female", v: 12 },
                  { label: "Female → male", v: -12 },
                  { label: "Original", v: 0 },
                ].map((tip) => (
                  <Pill
                    key={tip.label}
                    active={value === tip.v}
                    onClick={() => setPitch(v._id, tip.v)}
                  >
                    {tip.label}
                  </Pill>
                ))}
              </div>
            </div>
          );
        })}
      </Group>

      <Group title="Effect" hint="One at a time. Tap again to remove it.">
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
          <Pill
            active={!settings.selectedEffectId}
            onClick={() => setEffect(null)}
          >
            None
          </Pill>
        </div>
        {effectGroups.map((g) => {
          const list = effects.filter((e: any) =>
            e.displaySections?.includes(g.id),
          );
          if (!list.length) return null;
          return (
            <div key={g.id} className="tw-flex tw-flex-col tw-gap-1.5">
              <div className="tw-text-xs tw-font-extrabold tw-tracking-[0.04em] tw-text-jam-muted">
                {g.title.toUpperCase()}
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                {list.map((e: any) => (
                  <Pill
                    key={e._id}
                    active={settings.selectedEffectId === e._id}
                    onClick={() =>
                      setEffect(
                        settings.selectedEffectId === e._id ? null : e._id,
                      )
                    }
                  >
                    <span>{e.emoji}</span>
                    {e.name}
                  </Pill>
                ))}
              </div>
            </div>
          );
        })}
      </Group>

      <Group title="Autotune">
        <Switch
          checked={!!settings.autotune?.enabled}
          onChange={(on) =>
            dispatchSettings({ type: "SET_AUTOTUNE_ENABLED", payload: on })
          }
          title="Snap the vocals to key"
          hint="From a light polish to the obvious robotic sound."
        />
        {settings.autotune?.enabled && (
          <div className="tw-flex tw-gap-1.5">
            {AUTOTUNE_LEVELS.map((l) => (
              <Pill
                key={l.label}
                active={
                  // Nearest stop, so a strength set elsewhere still shows.
                  AUTOTUNE_LEVELS.reduce((a, b) =>
                    Math.abs(b.value - settings.autotune.strength) <
                    Math.abs(a.value - settings.autotune.strength)
                      ? b
                      : a,
                  ).value === l.value
                }
                onClick={() =>
                  dispatchSettings({
                    type: "SET_AUTOTUNE_STRENGTH",
                    payload: l.value,
                  })
                }
              >
                {l.label}
              </Pill>
            ))}
          </div>
        )}
      </Group>

      <Group title="Mix">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-min-w-0 tw-flex-grow">
            <span className="tw-block tw-text-[15px] tw-font-extrabold">
              Vocal volume
            </span>
            <span className="tw-block tw-text-xs tw-font-semibold tw-text-jam-muted">
              Louder or quieter than the music, in dB.
            </span>
          </span>
          <Stepper
            label="vocal volume"
            value={settings.voiceVolume}
            min={VOLUME_MIN}
            max={VOLUME_MAX}
            step={VOLUME_STEP}
            onChange={(v) =>
              dispatchSettings({ type: "SET_VOICE_VOLUME", payload: v })
            }
          />
        </div>
        <Switch
          checked={!!settings.extractBackground}
          onChange={(v) =>
            dispatchSettings({ type: "SET_EXTRACT_BACKGROUND", payload: v })
          }
          title="Split out backing vocals"
          hint="Cleaner result when the song has harmonies or ad-libs."
        />
        <Switch
          checked={!!settings.skipExtraction}
          onChange={(v) =>
            dispatchSettings({ type: "SET_SKIP_EXTRACTION", payload: v })
          }
          title="My audio is just vocals"
          hint="Skips separating the vocals from the music."
        />
      </Group>

      <Group title="Cover name">
        <input
          aria-label="Cover name"
          value={settings.name}
          onChange={(e) =>
            dispatchSettings({ type: "SET_NAME", payload: e.target.value })
          }
          placeholder={song?.title || "Optional"}
          className="tw-h-12 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-5 tw-text-[15px] tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted"
        />
      </Group>
    </div>
  );
};

export default SoundOptions;
